import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { GenericSizeBiases } from 'types/product';
import { track } from 'apis/amethyst';
import { HYDRA_PRODUCT_SIZING_PREDICTION } from 'constants/hydraTests';
import { sizeBiasImpression } from 'events/product';
import { useInAssignment } from 'hooks/useHydra';
import { selectPredictedSize } from 'selectors/product';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import useMartyContext from 'hooks/useMartyContext';
import HtmlToReact from 'components/common/HtmlToReact';
import MeasuringTapeGreenIcon from 'images/zappos/measuring-tape-green.svg';
import MeasuringTapeYellowIcon from 'images/zappos/measuring-tape-yellow.svg';

import css from 'styles/components/productdetail/genericSizeBiasReco.scss';

export interface GenericSizeBiasRecoProps {
  currentProductId: string;
  genericSizeBiases: {} | GenericSizeBiases;
  children?: React.ReactNode;
}

export const GenericSizeBiasReco = ({ currentProductId, genericSizeBiases, children }: GenericSizeBiasRecoProps) => {
  const { testId } = useMartyContext();
  const { productId, sizeBiases, text } = genericSizeBiases as GenericSizeBiases;
  const [copyText, setCopyText] = useState(null);

  const isHydraSizePrediction = useInAssignment(HYDRA_PRODUCT_SIZING_PREDICTION);
  const predictedSize = useSelector(selectPredictedSize);

  useEffect(() => {
    if (copyText && currentProductId === productId) {
      track(() => [sizeBiasImpression, { text: copyText, productId, sizeBiases }]);
    }
  }, [copyText, currentProductId, productId, sizeBiases, text]);

  const copyTextRef = useCallback(
    node => {
      if (node) {
        setCopyText(node.textContent);
      }
    },
    [setCopyText]
  );

  const copyProps = {
    'className': cn(isHydraSizePrediction ? css.sizingCopy : css.copy, { [css.predictedSize]: !!children && predictedSize }),
    'ref': copyTextRef,
    'data-test-id': testId('genericSizeBias')
  };

  const isAccurateSize = useMemo(() => text && text.includes('true to size'), [text]);

  return text && currentProductId === productId ? (
    <>
      <div className={cn({ [css.recommendedFitContainer]: isHydraSizePrediction })}>
        {isHydraSizePrediction && (
          <img className={css.recommendedFitIcon} src={isAccurateSize ? MeasuringTapeGreenIcon : MeasuringTapeYellowIcon} alt="Size Accuracy Icon" />
        )}
        <div className={cn({ [css.recommendedFitText]: isHydraSizePrediction && !!children })} data-test-id={testId('genericSizeBiasReco')}>
          <HtmlToReact {...copyProps}>{text}</HtmlToReact>
          {children}
        </div>
      </div>
    </>
  ) : null;
};

export default withErrorBoundary('GenericSizeBiasReco', GenericSizeBiasReco);
